import {useCallback, useEffect, useRef, useState} from 'react';
import {Navigate} from 'react-router-dom';
import {FreePlanBanner} from '../../components/FreePlanBanner';
import CampaignAvaliationModal from './components/CampaignAvaliationModal';

import Sidebar from './components/Sidebar';
import Chat from './components/Chat';
import Canvas from './components/Canvas';
import ConnectionBanner from './components/ConnectionBanner';
import LandingHero from './components/LandingHero';
import {AgentChatProvider, useAgentChatContext} from './context/AgentChatContext';
import {
  CampaignRuntimeProvider,
  useCampaignRuntimeContext,
} from './context/CampaignRuntimeContext';
import {usePaywall} from '../../contexts/PaywallContext/usePaywall';
import {getArtGenerationAvailability} from '../../utils/artConsumption';
import useAgentSessionStore from './store/agentSessionStore';
import {getAuthToken} from './services/authToken';
import {getStrategyDocumentFromSession} from './utils/sessionDocuments';
import styles from './styles.module.css';

function CampaignChatContent() {
  const {
    sessionDetail,
    isChatExpanded,
    onCampaignLimitReachedRef,
    error,
    clearError,
  } = useAgentChatContext();
  const {
    runtime,
    resetCampaignStrategy,
    resetArtScheduleData,
  } = useCampaignRuntimeContext();
  const {openPaywall, closePaywall} = usePaywall();
  const [isCampaignStartBlocked, setIsCampaignStartBlocked] = useState(false);
  const [selectedArt, setSelectedArt] = useState(null);
  const [artPreviewRenderKey, setArtPreviewRenderKey] = useState(0);
  const prevArtUrlsRef = useRef({});
  const activeSessionId = useAgentSessionStore((s) => s.activeSessionId);
  
  const strategyFromSession =
    activeSessionId && sessionDetail?.uuid === activeSessionId
      ? getStrategyDocumentFromSession(sessionDetail.documents)
      : '';

  const previewMarkdown = runtime.content.strategy;
  const showStrategyPreview = runtime.canvas.visible;
  const showActiveLayout = isChatExpanded || showStrategyPreview;

  useEffect(() => {
    resetCampaignStrategy();
    setSelectedArt(null);
    setArtPreviewRenderKey(0);
    prevArtUrlsRef.current = {};
    resetArtScheduleData();
  }, [
    activeSessionId,
    resetArtScheduleData,
    resetCampaignStrategy,
  ]);

  useEffect(() => {
    runtime.content.arts.forEach((art) => {
      const pieceNumber = art.pieceNumber ?? art.piece_number;
      if (!pieceNumber) {
        return;
      }
      const url = art.url ?? art.imageUrl;
      const revision = art.revision ?? null;
      const prev = prevArtUrlsRef.current[pieceNumber];
      if (
        prev &&
        (prev.url !== url || prev.revision !== revision)
      ) {
        setArtPreviewRenderKey((key) => key + 1);
      }
      prevArtUrlsRef.current[pieceNumber] = {url, revision};
    });
  }, [runtime.content.arts]);

  useEffect(() => {
    const overlayArts = runtime.liveOverlay?.arts ?? {};
    Object.values(overlayArts).forEach((overlayArt) => {
      const pieceNumber = overlayArt?.pieceNumber;
      if (!pieceNumber) {
        return;
      }
      const url = overlayArt.imageUrl ?? overlayArt.url;
      const revision = overlayArt.revision ?? null;
      const prev = prevArtUrlsRef.current[pieceNumber];
      if (
        revision &&
        prev &&
        (prev.url !== url || prev.revision !== revision)
      ) {
        setArtPreviewRenderKey((key) => key + 1);
      }
      if (url) {
        prevArtUrlsRef.current[pieceNumber] = {url, revision};
      }
    });
  }, [runtime.liveOverlay?.arts]);

  const handleCampaignLimitReached = useCallback(
    () => {
      setIsCampaignStartBlocked(true);
      openPaywall({
        reason: 'campaign_art_limit_reached',
        returnTo: window.location.pathname,
        onDismiss: async () => {
          const availability = await getArtGenerationAvailability(5, {
            allowCampaignGeneration: true,
          });
          if (availability.hasAvailableArts) {
            setIsCampaignStartBlocked(false);
          }
          window.dispatchEvent(new CustomEvent('user-plan-count-updated'));
          closePaywall();
        },
      });
    },
    [openPaywall, closePaywall],
  );

  useEffect(() => {
    onCampaignLimitReachedRef.current = handleCampaignLimitReached;
  }, [handleCampaignLimitReached, onCampaignLimitReachedRef]);

  useEffect(() => {
    if (!isCampaignStartBlocked && error) {
      clearError();
    }
  }, [isCampaignStartBlocked, error, clearError]);

  if (!getAuthToken()) {
    return <Navigate to="/SignIn" replace />;
  }

  return (
    <div
      className={`${styles.landingPage_chat} ${showStrategyPreview ? styles.landingPage_chatWhite : ''}`}
    >
      <div className={styles.gradientBackground} aria-hidden="true" />
      {!showStrategyPreview && (
        <>
          <div className={styles.backgroundBlur2} aria-hidden="true" />
          <div className={styles.backgroundBlur3} aria-hidden="true" />
        </>
      )}
      <FreePlanBanner />

      <div className={styles.container}>
        <div
          className={`${styles.bodyWithStepper} ${
            showActiveLayout ? styles.bodyWithStepperActive : ''
          }`}
        >
          <Sidebar />
          <div
            className={`${styles.pageLayout} ${
              showActiveLayout ? styles.pageLayoutActive : ''
            }`}
          >
            <div
              className={
                showStrategyPreview
                  ? styles.pageLayoutWithCanvas
                  : styles.chatColumn
              }
            >
              <div className={styles.chatColumn}>
                <div
                  className={`${styles.heroSection} ${
                    isChatExpanded ? styles.heroSectionHidden : ''
                  }`}
                >
                  <LandingHero />
                </div>
                {isChatExpanded && <ConnectionBanner />}
                <Chat
                  isCampaignStartBlocked={isCampaignStartBlocked}
                  selectedArt={selectedArt}
                  setSelectedArt={setSelectedArt}
                />
              </div>
              {showStrategyPreview && (
                <div className={styles.canvasColumn}>
                  <Canvas
                    key={activeSessionId || 'no-session'}
                    content={previewMarkdown}
                    strategyFromSession={strategyFromSession}
                    selectedArt={selectedArt}
                    setSelectedArt={setSelectedArt}
                    imageRenderKey={artPreviewRenderKey}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <CampaignAvaliationModal sessionUuid={sessionDetail?.uuid} sessionStatus={sessionDetail?.status} />
    </div>
  );
}

export default function CampaignChatPage() {
  useEffect(() => {
    return () => {
      useAgentSessionStore.getState().reset();
    };
  }, []);

  return (
    <AgentChatProvider>
      <CampaignRuntimeProvider>
        <CampaignChatContent />
      </CampaignRuntimeProvider>
    </AgentChatProvider>
  );
}
